import React from 'react';
import { motion } from 'framer-motion';

const SkeletonLoader = ({ variant = 'task', count = 5 }) => {
  const items = Array.from({ length: count }, (_, i) => i);

  if (variant === 'task') {
    return (
      <div className="space-y-3">
        {items.map((i) => (
          <TaskSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (variant === 'project') {
    return (
      <div className="space-y-2">
        {items.map((i) => (
          <ProjectSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (variant === 'dashboard') {
    return <DashboardSkeleton />;
  }

  return null;
};

const TaskSkeleton = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm border border-slate-200 dark:border-slate-700"
    >
      <div className="flex items-start gap-3">
        {/* Checkbox skeleton */}
        <div className="w-5 h-5 rounded bg-slate-200 dark:bg-slate-700 animate-shimmer" />

        <div className="flex-1 space-y-2">
          {/* Title skeleton */}
          <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded animate-shimmer w-3/4" />

          {/* Description skeleton */}
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-shimmer w-1/2" />

          {/* Tags skeleton */}
          <div className="flex gap-2 mt-2">
            <div className="h-6 w-16 bg-slate-200 dark:bg-slate-700 rounded-full animate-shimmer" />
            <div className="h-6 w-20 bg-slate-200 dark:bg-slate-700 rounded-full animate-shimmer" />
          </div>
        </div>

        {/* Priority indicator skeleton */}
        <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 animate-shimmer" />
      </div>
    </motion.div>
  );
};

const ProjectSkeleton = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex items-center gap-3 p-3 rounded-lg bg-slate-100 dark:bg-slate-800"
    >
      {/* Color badge skeleton */}
      <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 animate-shimmer" />

      {/* Project name skeleton */}
      <div className="flex-1 h-5 bg-slate-200 dark:bg-slate-700 rounded animate-shimmer" />

      {/* Task count skeleton */}
      <div className="w-12 h-5 bg-slate-200 dark:bg-slate-700 rounded animate-shimmer" />
    </motion.div>
  );
};

const DashboardSkeleton = () => {
  return (
    <div className="space-y-6">
      {/* Stats cards skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm border border-slate-200 dark:border-slate-700"
          >
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded animate-shimmer w-1/2 mb-4" />
            <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded animate-shimmer w-3/4" />
          </motion.div>
        ))}
      </div>

      {/* Recent tasks skeleton */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm border border-slate-200 dark:border-slate-700">
        <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded animate-shimmer w-1/4 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-slate-200 dark:bg-slate-700 rounded animate-shimmer" />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SkeletonLoader;
